import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Globe, Plus, Trash2, Activity, Send, ChevronDown, ChevronUp,
  Check, X, Loader2, RefreshCw, AlertTriangle, Eye, EyeOff, Shield, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/I18nContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ALL_EVENTS = [
  { id: 'booking.created', label: 'Prenotazione Creata', desc: 'Nuova prenotazione (da portale o API)' },
  { id: 'booking.confirmed', label: 'Prenotazione Confermata', desc: 'Quando lo staff conferma la prenotazione' },
  { id: 'booking.cancelled', label: 'Prenotazione Cancellata', desc: 'Cancellazione da qualsiasi fonte' },
  { id: 'booking.checkin', label: 'Check-in Eseguito', desc: 'Apertura serratura al check-in' },
  { id: 'access.command', label: 'Comando Accesso', desc: 'Lock/unlock da API o pannello' },
  { id: 'guest_key.generated', label: 'Chiave Ospite Generata', desc: 'QR code / link condiviso con ospite' },
];

function StatusBadge({ success }) {
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded font-medium"
      style={{
        background: success ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
        color: success ? '#10b981' : '#ef4444',
        border: `1px solid ${success ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
      }}>
      {success ? '200 OK' : 'FAILED'}
    </span>
  );
}

function LogsPanel({ webhookId, headers, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replayingId, setReplayingId] = useState(null);
  const { t } = useI18n();

  useEffect(() => {
    axios.get(`${API}/api/webhooks/${webhookId}/logs`, { headers })
      .then(r => setLogs(r.data.logs || []))
      .catch(() => toast.error('Errore caricamento log'))
      .finally(() => setLoading(false));
  }, [webhookId]);

  const handleReplay = async (log) => {
    if (!log.log_id) { toast.error('Log ID non disponibile per il replay.'); return; }
    setReplayingId(log.log_id);
    try {
      const res = await axios.post(`${API}/api/webhooks/${webhookId}/replay/${log.log_id}`, {}, { headers });
      if (res.data.success) {
        toast.success(t('webhooks_retry_success') + ` (${res.data.status_code})`);
      } else {
        toast.error(`${t('webhooks_retry_fail')} (${res.data.status_code || 'timeout'})`);
      }
      // Ricarica i log dopo il replay
      const r = await axios.get(`${API}/api/webhooks/${webhookId}/logs`, { headers });
      setLogs(r.data.logs || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('webhooks_retry_fail'));
    } finally {
      setReplayingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.8)' }}>
      <div className="w-full max-w-2xl rounded-sm max-h-[80vh] flex flex-col"
        style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0"
          style={{ borderColor: 'var(--lux-border-1)' }}>
          <div>
            <p className="font-medium text-sm" style={{ color: 'var(--lux-text)' }}>Log Delivery</p>
            <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>{t('webhooks_last_events')} {logs.length} eventi</p>
          </div>
          <button onClick={onClose} style={{ color: 'var(--lux-text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-5">
          {loading ? (
            <div className="text-center py-8" style={{ color: 'var(--lux-text-muted)' }}>
              <div className="w-5 h-5 border-2 rounded-full animate-spin mx-auto"
                style={{ borderColor: 'var(--lux-gold)', borderTopColor: 'transparent' }} />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: 'var(--lux-text-muted)' }}>
              {t('webhooks_no_logs')}
            </p>
          ) : (
            <div className="space-y-2">
              {logs.map((log, i) => (
                <div key={log.log_id || i} className="p-3 rounded-sm text-xs"
                  style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)' }}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <StatusBadge success={log.success} />
                      <code style={{ color: 'var(--lux-gold)' }}>{log.event}</code>
                    </div>
                    <div className="flex items-center gap-2">
                      <span style={{ color: 'var(--lux-text-muted-3)' }}>
                        {new Date(log.triggered_at).toLocaleString('it-IT')}
                        {log.attempt > 1 && ` · tentativo ${log.attempt}`}
                      </span>
                      {!log.success && log.log_id && (
                        <button
                          data-testid={`replay-btn-${log.log_id}`}
                          onClick={() => handleReplay(log)}
                          disabled={replayingId === log.log_id}
                          title={t('webhooks_retry')}
                          className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors"
                          style={{ background: 'rgba(201,168,76,0.1)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.25)', cursor: 'pointer' }}
                        >
                          {replayingId === log.log_id
                            ? <Loader2 size={10} className="animate-spin" />
                            : <RotateCcw size={10} />}
                          {t('webhooks_retry')}
                        </button>
                      )}
                    </div>
                  </div>
                  {log.response_preview && (
                    <p className="font-mono mt-1 truncate" style={{ color: 'var(--lux-text-muted)' }}>
                      {log.response_preview}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WebhookCard({ wh, headers, onDelete, onToggle, onRefresh }) {
  const [testing, setTesting] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/api/webhooks/${wh.webhook_id}/test`, {}, { headers });
      if (data.success) {
        toast.success(`Test riuscito! Status: ${data.status_code}`);
      } else {
        toast.error(`Test fallito (${data.status_code || 'timeout'}): ${data.response_preview?.slice(0, 80)}`);
      }
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel test.');
    } finally {
      setTesting(false);
    }
  };

  const del = async () => {
    if (!window.confirm('Eliminare questo webhook?')) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/webhooks/${wh.webhook_id}`, { headers });
      toast.success('Webhook eliminato.');
      onDelete();
    } catch {
      toast.error('Errore nell\'eliminazione.');
    } finally {
      setDeleting(false);
    }
  };

  const toggle = async () => {
    try {
      await axios.patch(`${API}/api/webhooks/${wh.webhook_id}`, { is_active: !wh.is_active }, { headers });
      toast.success(wh.is_active ? 'Webhook disabilitato.' : 'Webhook abilitato.');
      onRefresh();
    } catch {
      toast.error('Errore.');
    }
  };

  return (
    <>
      <div className="rounded-sm overflow-hidden" style={{ background: 'var(--lux-surface-1)', border: `1px solid ${wh.is_active ? 'var(--lux-border-1)' : 'rgba(107,114,128,0.2)'}`, opacity: wh.is_active ? 1 : 0.6 }}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4">
          <div className="w-8 h-8 rounded flex items-center justify-center shrink-0"
            style={{ background: wh.is_active ? 'rgba(99,102,241,0.1)' : 'rgba(107,114,128,0.1)' }}>
            <Globe size={14} style={{ color: wh.is_active ? '#6366f1' : '#6b7280' }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium" style={{ color: 'var(--lux-text)' }}>{wh.label}</span>
              {!wh.is_active && (
                <span className="text-[10px] px-1.5 py-0.5 rounded"
                  style={{ background: 'rgba(107,114,128,0.15)', color: '#6b7280' }}>Disabilitato</span>
              )}
            </div>
            <p className="text-xs mt-0.5 truncate font-mono" style={{ color: 'var(--lux-text-muted)' }}>{wh.url}</p>
          </div>

          {/* Stats */}
          <div className="hidden sm:flex items-center gap-4 text-xs shrink-0" style={{ color: 'var(--lux-text-muted-3)' }}>
            <span style={{ color: '#10b981' }}>✓ {wh.success_count}</span>
            <span style={{ color: '#ef4444' }}>✗ {wh.failure_count}</span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={test} disabled={testing}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded"
              style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted)', border: '1px solid var(--lux-border-2)' }}
              title="Invia test">
              {testing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Test
            </button>
            <button onClick={() => setShowLogs(true)}
              className="p-1.5 rounded" style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted)', border: '1px solid var(--lux-border-2)' }}
              title="Log delivery">
              <Activity size={13} />
            </button>
            <button onClick={toggle}
              className="p-1.5 rounded" style={{ background: 'var(--lux-surface-3)', color: wh.is_active ? 'var(--lux-gold)' : 'var(--lux-text-muted-3)', border: '1px solid var(--lux-border-2)' }}
              title={wh.is_active ? 'Disabilita' : 'Abilita'}>
              {wh.is_active ? <Check size={13} /> : <X size={13} />}
            </button>
            <button onClick={del} disabled={deleting}
              className="p-1.5 rounded" style={{ color: '#ef4444', background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.15)' }}
              title="Elimina">
              {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={13} />}
            </button>
            <button onClick={() => setExpanded(!expanded)}
              className="p-1.5 rounded" style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted)', border: '1px solid var(--lux-border-2)' }}>
              {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
          </div>
        </div>

        {/* Expanded: events list */}
        {expanded && (
          <div className="px-5 pb-4 pt-0 border-t" style={{ borderColor: 'var(--lux-border-1)' }}>
            <p className="text-[10px] uppercase tracking-widest mb-2 mt-3" style={{ color: 'var(--lux-text-muted)' }}>
              Eventi attivi ({wh.events?.length || 0})
            </p>
            <div className="flex flex-wrap gap-2">
              {(wh.events || []).map(ev => {
                const evDef = ALL_EVENTS.find(e => e.id === ev);
                return (
                  <span key={ev} className="text-[10px] px-2 py-0.5 rounded-full"
                    style={{ background: 'rgba(99,102,241,0.1)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.2)' }}>
                    {evDef?.label || ev}
                  </span>
                );
              })}
            </div>
            {wh.last_triggered_at && (
              <p className="text-[10px] mt-3" style={{ color: 'var(--lux-text-muted-3)' }}>
                Ultimo evento: {new Date(wh.last_triggered_at).toLocaleString('it-IT')}
              </p>
            )}
            <div className="mt-3 p-3 rounded-sm text-xs font-mono"
              style={{ background: '#0a0a0a', color: '#a8b5c8', border: '1px solid var(--lux-border-2)' }}>
              <p style={{ color: 'var(--lux-text-muted-3)', marginBottom: '4px' }}># Verifica la firma HMAC nel tuo endpoint:</p>
              <p>X-Shangrilux-Signature: sha256=...</p>
              <p>X-Shangrilux-Event: booking.created</p>
              <p>X-Shangrilux-Delivery: {wh.webhook_id}-1</p>
            </div>
          </div>
        )}
      </div>

      {showLogs && (
        <LogsPanel webhookId={wh.webhook_id} headers={headers} onClose={() => setShowLogs(false)} />
      )}
    </>
  );
}

function CreateWebhookModal({ headers, onClose, onCreated }) {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [selectedEvents, setSelectedEvents] = useState(['booking.created', 'booking.confirmed']);
  const [loading, setLoading] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);

  const toggleEvent = (id) => {
    setSelectedEvents(prev =>
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  const submit = async () => {
    if (!url.trim()) { toast.error('Inserisci l\'URL del webhook.'); return; }
    if (selectedEvents.length === 0) { toast.error('Seleziona almeno un evento.'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/webhooks`, {
        url: url.trim(), label: label.trim(), events: selectedEvents,
      }, { headers });
      setCreatedKey(data.webhook?.secret);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella creazione.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)',
    color: 'var(--lux-text)', borderRadius: '2px', padding: '10px 14px',
    fontSize: '13px', width: '100%', outline: 'none',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.8)' }}>
      <div className="w-full max-w-lg rounded-sm"
        style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: 'var(--lux-border-1)' }}>
          <h3 className="font-serif text-lg font-light" style={{ color: 'var(--lux-text)' }}>
            Nuovo Webhook
          </h3>
          <button onClick={onClose} style={{ color: 'var(--lux-text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {createdKey ? (
            <div>
              <div className="p-4 rounded-sm mb-4"
                style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.25)' }}>
                <p className="text-sm font-medium mb-2" style={{ color: '#059669' }}>
                  Webhook creato — salva il secret!
                </p>
                <p className="text-xs mb-3" style={{ color: 'var(--lux-text-muted)' }}>
                  Usa questo secret per verificare la firma HMAC-SHA256 dei payload.
                </p>
                <code className="block text-xs p-2 rounded break-all"
                  style={{ background: '#0a0a0a', color: '#e5e5e5' }}>
                  {createdKey}
                </code>
              </div>
              <button onClick={() => onCreated()}
                className="w-full py-3 text-sm font-medium"
                style={{ background: 'var(--lux-gold)', color: '#000', border: 'none', cursor: 'pointer' }}>
                Ho salvato il secret — chiudi
              </button>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'var(--lux-text-muted)' }}>
                  URL Endpoint <span style={{ color: 'var(--lux-gold)' }}>*</span>
                </label>
                <input data-testid="webhook-url-input" type="url" placeholder="https://tuosistema.io/webhook/shangrilux"
                  value={url} onChange={e => setUrl(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'var(--lux-text-muted)' }}>Nome</label>
                <input data-testid="webhook-label-input" type="text" placeholder="es. PMS Mews Production"
                  value={label} onChange={e => setLabel(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--lux-text-muted)' }}>
                  Eventi <span style={{ color: 'var(--lux-gold)' }}>*</span>
                </label>
                <div className="space-y-2">
                  {ALL_EVENTS.map(ev => (
                    <label key={ev.id} className="flex items-start gap-3 cursor-pointer group">
                      <input type="checkbox" checked={selectedEvents.includes(ev.id)}
                        onChange={() => toggleEvent(ev.id)}
                        className="mt-0.5 shrink-0" style={{ accentColor: 'var(--lux-gold)' }} />
                      <div>
                        <p className="text-xs font-medium" style={{ color: 'var(--lux-text-sec)' }}>{ev.label}</p>
                        <p className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>{ev.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={onClose} className="flex-1 py-2.5 text-sm"
                  style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)', background: 'none', cursor: 'pointer' }}>
                  Annulla
                </button>
                <button data-testid="create-webhook-btn" onClick={submit} disabled={loading}
                  className="flex-1 py-2.5 text-sm font-medium flex items-center justify-center gap-2"
                  style={{ background: 'var(--lux-gold)', color: '#000', border: 'none', cursor: 'pointer' }}>
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  Crea Webhook
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function Webhooks({ token, tenantSlug }) {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const headers = {
    Authorization: `Bearer ${token}`,
    'X-Tenant-Slug': tenantSlug,
  };

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/webhooks`, { headers });
      setWebhooks(data.webhooks || []);
    } catch {
      toast.error('Errore caricamento webhook.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="webhooks-section">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif text-2xl font-light" style={{ color: 'var(--lux-text)' }}>Webhook Outbound</h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--lux-text-muted)' }}>
            Notifiche HTTP verso sistemi esterni (PMS, CRM, automazioni) · Max 10 webhook
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded"
            style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
            <RefreshCw size={13} /> Aggiorna
          </button>
          <button data-testid="add-webhook-btn" onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium"
            style={{ background: 'var(--lux-gold)', color: '#000' }}>
            <Plus size={14} /> Aggiungi Webhook
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div className="p-4 rounded-sm mb-6" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)' }}>
        <div className="flex items-start gap-3">
          <Shield size={14} style={{ color: '#818cf8', marginTop: 1, flexShrink: 0 }} />
          <div className="text-xs" style={{ color: 'var(--lux-text-muted)', lineHeight: 1.6 }}>
            Ogni payload include l'header <code style={{ color: '#c9a84c' }}>X-Shangrilux-Signature: sha256=...</code> per verifica HMAC-SHA256.
            Retry automatico 3 volte con backoff esponenziale. Consegna entro 30 secondi dall'evento.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16" style={{ color: 'var(--lux-text-muted)' }}>
          <div className="w-5 h-5 border-2 rounded-full animate-spin mx-auto"
            style={{ borderColor: 'var(--lux-gold)', borderTopColor: 'transparent' }} />
        </div>
      ) : webhooks.length === 0 ? (
        <div className="text-center py-16" style={{ color: 'var(--lux-text-muted)' }}>
          <Globe size={36} className="mx-auto mb-4 opacity-20" />
          <p className="text-sm mb-2">Nessun webhook configurato.</p>
          <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
            Aggiungi il primo webhook per integrare il tuo PMS o sistema di automazione.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="webhooks-list">
          {webhooks.map(wh => (
            <WebhookCard
              key={wh.webhook_id}
              wh={wh}
              headers={headers}
              onDelete={load}
              onRefresh={load}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateWebhookModal
          headers={headers}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}
